<template>
  <div class="watermark-container" ref="containerRef">
    <slot></slot>
  </div>
</template>

<script>
// 判断是否为数字
const isNumber = (obj) => Object.prototype.toString.call(obj) === '[object Number]';

// 转化为数字
const toNumber = (value, defaultValue) => {
  if (value === undefined || value === null || value === '') return defaultValue;
  if (isNumber(value)) return value;
  const numberVal = parseFloat(value);
  return isNumber(numberVal) ? numberVal : defaultValue;
};

// 默认配置
const defaultOptions = {
  rotate: -20,
  zIndex: 1,
  width: 100,
  gap: [100, 100],
  fontStyle: {
    fontSize: '16px',
    color: 'rgba(0, 0, 0, 0.15)',
    fontFamily: 'sans-serif',
    fontWeight: 'normal',
  },
};

// 合并配置
const getMergedOptions = (option) => {
  const options = option || {};
  const mergedOptions = {
    ...options,
    rotate: options.rotate ?? defaultOptions.rotate,
    zIndex: options.zIndex ?? defaultOptions.zIndex,
    fontStyle: { ...defaultOptions.fontStyle, ...options.fontStyle },
    width: toNumber(options.width, options.image ? defaultOptions.width : undefined),
    height: toNumber(options.height, undefined),
    gap: [
      toNumber(options.gap?.[0], defaultOptions.gap[0]),
      toNumber(options.gap?.[1] ?? options.gap?.[0], defaultOptions.gap[1]),
    ],
  };

  const mergedOffsetX = toNumber(mergedOptions.offset?.[0], 0);
  const mergedOffsetY = toNumber(mergedOptions.offset?.[1] ?? mergedOptions.offset?.[0], 0);
  mergedOptions.offset = [mergedOffsetX, mergedOffsetY];

  return mergedOptions;
};

// 绘制 canvas，返回 base64 图片及尺寸
const getCanvasData = (options) => {
  const { rotate, image, content, fontStyle, gap } = options;
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  const ratio = window.devicePixelRatio;

  /** 画布配置 */
  const configCanvas = (size) => {
    const canvasWidth = gap[0] + size.width;
    const canvasHeight = gap[1] + size.height;

    canvas.setAttribute('width', `${canvasWidth * ratio}px`);
    canvas.setAttribute('height', `${canvasHeight * ratio}px`);
    canvas.style.width = `${canvasWidth}px`;
    canvas.style.height = `${canvasHeight}px`;

    // 重新映射画布上的 (0,0) 位置
    ctx.translate((canvasWidth * ratio) / 2, (canvasHeight * ratio) / 2);
    // 缩放当前绘图至更大或更小
    ctx.scale(ratio, ratio);
    // 旋转当前绘图
    ctx.rotate((rotate * Math.PI) / 180);
  };

  /**
   * 计算每一行的行高、行宽。
   * 在行宽的一半的位置开始绘制文字，行内每个文字的位置是行高的一半 * index
   */
  const measureTextSize = (ctx, content, rotate) => {
    let width = 0;
    let height = 0;
    const lineSize = [];
    content.forEach((item) => {
      const { width: textWidth, fontBoundingBoxAscent, fontBoundingBoxDescent } = ctx.measureText(item);
      const textHeight = fontBoundingBoxAscent + fontBoundingBoxDescent;
      if (textWidth > width) width = textWidth;
      height += textHeight;
      lineSize.push({ height: textHeight, width: textWidth });
    });

    const angle = (rotate * Math.PI) / 180;

    return {
      originWidth: width,
      originHeight: height,
      width: Math.ceil(Math.abs(Math.sin(angle) * height) + Math.abs(Math.cos(angle) * width)),
      height: Math.ceil(Math.abs(Math.sin(angle) * width) + Math.abs(Math.cos(angle) * height)),
      lineSize,
    };
  };

  /** 绘画文字 */
  const drawText = () => {
    const { fontSize, color, fontWeight, fontFamily } = fontStyle;
    const realFontSize = toNumber(fontSize, 0) || fontStyle.fontSize;
    const contentArr = Array.isArray(content) ? content : [content];

    // 先设置字体用于测量（configCanvas 会重置 ctx 状态，故测量前先设置一次）
    ctx.font = `${fontWeight} ${realFontSize}px ${fontFamily}`;
    const measureSize = measureTextSize(ctx, contentArr, rotate);

    const width = options.width || measureSize.width;
    const height = options.height || measureSize.height;
    configCanvas({ width, height });

    // configCanvas 会重置 canvas 上下文，这里重新设置字体与样式
    ctx.fillStyle = color;
    ctx.font = `${fontWeight} ${realFontSize}px ${fontFamily}`;
    ctx.textBaseline = 'top';

    contentArr.forEach((item, index) => {
      const { height: lineHeight, width: lineWidth } = measureSize.lineSize[index];
      const xStartPoint = -lineWidth / 2;
      const yStartPoint = -(options.height || measureSize.originHeight) / 2 + lineHeight * index;
      ctx.fillText(item, xStartPoint, yStartPoint, options.width || measureSize.originWidth);
    });

    return Promise.resolve({ base64Url: canvas.toDataURL(), height, width });
  };

  /** 绘画图片 */
  const drawImage = () => {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.referrerPolicy = 'no-referrer';
      img.src = image;

      img.onload = () => {
        let { width, height } = options;
        if (!width || !height) {
          if (width) {
            height = (img.height / img.width) * +width;
          } else {
            width = (img.width / img.height) * +height;
          }
        }
        configCanvas({ width, height });
        ctx.drawImage(img, -width / 2, -height / 2, width, height);
        resolve({ base64Url: canvas.toDataURL(), width, height });
      };

      img.onerror = () => resolve(drawText());
    });
  };

  return image ? drawImage() : drawText();
};

export default {
  name: 'Watermark',
  props: {
    // 水印层级
    zIndex: { type: [String, Number], default: 1 },
    // 单个水印宽度
    width: { type: Number, default: undefined },
    // 单个水印高度
    height: { type: Number, default: undefined },
    // 旋转角度
    rotate: { type: Number, default: -20 },
    // 图片水印地址（与 content 二选一，优先图片）
    image: { type: String, default: '' },
    // 文字水印内容
    content: { type: [String, Array], default: '' },
    // 字体样式
    fontStyle: {
      type: Object,
      default: () => ({}),
    },
    // 两个水印之间的空白距离 [x, y]
    gap: { type: Array, default: () => [100, 100] },
    // 水印相对于容器的偏移量 [x, y]
    offset: { type: Array, default: undefined },
    // 自定义容器（默认使用组件根元素）
    getContainer: { type: Function, default: undefined },
  },
  data() {
    return {
      watermarkDiv: null,
      mutationObserver: null,
    };
  },
  computed: {
    // 监听用 key，依赖变化时触发重绘
    watermarkKey() {
      return JSON.stringify({
        content: this.content,
        image: this.image,
        rotate: this.rotate,
        width: this.width,
        height: this.height,
        gap: this.gap,
        offset: this.offset,
        fontStyle: this.fontStyle,
        zIndex: this.zIndex,
      });
    },
  },
  watch: {
    watermarkKey() {
      this.drawWatermark();
    },
  },
  mounted() {
    this.drawWatermark();
  },
  beforeDestroy() {
    this.destroyWatermark();
  },
  methods: {
    // 获取容器元素
    getContainerEl() {
      return this.getContainer ? this.getContainer() : this.$refs.containerRef;
    },
    // 构建合并后的配置
    buildOptions() {
      return getMergedOptions({
        zIndex: this.zIndex,
        width: this.width,
        height: this.height,
        rotate: this.rotate,
        image: this.image,
        content: this.content,
        fontStyle: this.fontStyle,
        gap: this.gap,
        offset: this.offset,
        getContainer: this.getContainerEl,
      });
    },
    // 绘制水印
    drawWatermark() {
      const container = this.getContainerEl();
      if (!container) return;
      // 没有内容且没有图片时移除已有水印
      const hasContent = Array.isArray(this.content)
        ? this.content.some((c) => c && String(c).trim())
        : this.content && String(this.content).trim();
      if (!hasContent && !this.image) {
        this.destroyWatermark();
        return;
      }

      const mergedOptions = this.buildOptions();
      getCanvasData(mergedOptions).then(({ base64Url, width, height }) => {
        const offsetLeft = mergedOptions.offset[0] + 'px';
        const offsetTop = mergedOptions.offset[1] + 'px';
        const wmStyle = `
          width: calc(100% - ${offsetLeft});
          height: calc(100% - ${offsetTop});
          position: absolute;
          top: ${offsetTop};
          left: ${offsetLeft};
          bottom: 0;
          right: 0;
          pointer-events: none;
          z-index: ${mergedOptions.zIndex};
          background-position: 0 0;
          background-size: ${mergedOptions.gap[0] + width}px ${mergedOptions.gap[1] + height}px;
          background-repeat: repeat;
          background-image: url(${base64Url});
        `;

        // 没有水印 div 时创建
        if (!this.watermarkDiv) {
          const div = document.createElement('div');
          this.watermarkDiv = div;
          container.appendChild(div);
          container.style.position = 'relative';
        }
        this.watermarkDiv.setAttribute('style', wmStyle.trim());

        // 监听容器变化，防止水印被手动删除或篡改
        this.mutationObserver && this.mutationObserver.disconnect();
        this.mutationObserver = new MutationObserver((mutations) => {
          const isChanged = mutations.some((mutation) => {
            let flag = false;
            if (mutation.removedNodes.length) {
              flag = Array.from(mutation.removedNodes).some((node) => node === this.watermarkDiv);
            }
            if (mutation.type === 'attributes' && mutation.target === this.watermarkDiv) {
              flag = true;
            }
            return flag;
          });
          if (isChanged) {
            this.watermarkDiv = null;
            this.drawWatermark();
          }
        });
        this.mutationObserver.observe(container, {
          attributes: true,
          subtree: true,
          childList: true,
        });
      });
    },
    // 销毁水印
    destroyWatermark() {
      this.mutationObserver && this.mutationObserver.disconnect();
      this.mutationObserver = null;
      if (this.watermarkDiv && this.watermarkDiv.parentNode) {
        this.watermarkDiv.parentNode.removeChild(this.watermarkDiv);
      }
      this.watermarkDiv = null;
    },
  },
};
</script>

<style lang="scss" scoped>
.watermark-container {
  position: relative;
  width: 100%;
  height: 100%;
}
</style>
